import React, {useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import * as MainAssets from '../../../assets/images/images';
import * as Components from '../../../components';
import CreateFlowModal from './CreateFlowModal';
import * as Actions from '../../../store/actions';
import {useFlowsLogic} from './hooks/useFlows';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import PageNumber from 'components/PageNumber';
import {Constants} from '../../../helpers';

function FraudDetectionFlows() {
  const navigate = useNavigate();
  const [openFlowModal, setOpenFlowModal] = useState(false);
  const {
    currentPage,
    pageClick,
    manageFlows,
    flows,
    isLoadingFlows: isLoading,
    setLimit,
    companyId,
    easyFlows,
    limit,
  } = useFlowsLogic({enabled: true});

  const handleIconClick = (flow, route) => {
    navigate(`${route}/${flow.id}`, {
      state: {
        flowName: flow?.name || '-',
        flowData: flow,
      },
    });
    ['currentFlowData', 'taskId', 'ruleIndex', 'isModified'].forEach(key =>
      localStorage.removeItem(key),
    );
    localStorage.setItem('currentFlowData', JSON.stringify(flow));
  };

  return (
    <Components.DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Fraud Flows</p>
      }
    >
      <CreateFlowModal
        open={openFlowModal}
        setOpen={setOpenFlowModal}
        companyId={companyId}
      />
      <div className="flex items-center py-2 flex-wrap gap-3 justify-end mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <Components.SecondaryButton
          buttonText="Create with template"
          onClick={() => navigate(Constants.PAGES_URL.FLOW_TEMPLATES)}
        />
        <Components.PrimaryButton
          buttonText="Create a flow"
          onClick={() => setOpenFlowModal(true)}
          disabled={!manageFlows}
        />
      </div>

      <section className="flex flex-col">
        <div className="flex  justify-between flex-col w-full mb-6"></div>

        <Components.TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                <span>Name of flow</span>
              </th>

              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <span>USe cases</span>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <span>date created</span>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <span>STATUS</span>
              </th>
              <th className="min-w-[60px]"></th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Components.Loader height={45} />
                ) : !isLoading && flows && flows?.flows?.length === 0 ? (
                  <Components.EmptyState
                    height={45}
                    buttonText="Create a flow"
                    onClick={() => setOpenFlowModal(true)}
                    heading="No record found!"
                    body="You currently don't have any flows, create one to start setting up rules"
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {!isLoading &&
              flows &&
              flows?.flows?.map(flow => (
                <tr
                  className="border-[0.5px] border-[#E4E3EC]"
                  key={flow.id}
                  style={{
                    pointerEvents: manageFlows ? 'auto' : 'none',
                  }}
                >
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {flow?.name || '-'}
                  </td>

                  <td className="capitalize p-4 pl-0 text-body whitespace-nowrap">
                    {flow?.usecase || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(flow.createdAt).format('Do MMM YYYY hh:mm')}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium ${
                        getStatusTextAndStyles(flow?.status).solid
                      }  rounded-full flex items-center w-fit`}
                    >
                      <span className="-mb-[2px]">
                        {getStatusTextAndStyles(flow?.status).text || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap relative">
                    <div className="flex items-center gap-10 z-2 p-5 absolute top-0">
                      {/*<SettingsToggle />*/}
                      <span
                        title="View"
                        className="h-4 cursor-pointer aspect-square"
                        onClick={() => handleIconClick(flow, '/flows/analysis')}
                      >
                        <img src={MainAssets?.eye} alt="" />
                      </span>

                      <span
                        title="Manage Rules"
                        className="h-4 cursor-pointer aspect-square"
                        onClick={() =>
                          handleIconClick(flow, '/flows/edit-process')
                        }
                      >
                        <img src={MainAssets.edit} alt="" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </Components.TableLayout>

        {flows && flows?.flows?.length !== 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={currentPage}
              limit={limit}
              totalCount={easyFlows?.pages || 0}
            />
            <div className="flex items-center gap-5">
              <Components.PageLimit
                onLimitChange={setLimit}
                length={flows && flows?.flows?.length}
                limit={limit}
              />
              <Components.Pagination
                total={easyFlows && Math.ceil(easyFlows?.pages / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  pageClick(activePage);
                  const searchParams = new URLSearchParams(
                    document.location.search,
                  );
                  searchParams.set('page', activePage);
                  const newSearchParams = searchParams.toString();
                  navigate(`?${newSearchParams}`);
                }}
              />
            </div>
          </div>
        )}
      </section>
    </Components.DashboardLayout>
  );
}

export default connect(state => state, Actions)(FraudDetectionFlows);
